import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react"
import { AnalyticsBrowser } from "@segment/analytics-next"
import { useParams } from "@tanstack/react-router"
import { useAccount } from "wagmi"

import {
  selectUsername,
  selectWalletAddress,
  useDappSelector,
} from "#/redux-state"
import { MARKET_ITEMS, SEGMENT_KEY } from "#/shared/constants"

import {
  BorrowCollateralChangePosthogEventProps,
  BorrowRepayPosthogEventProps,
  BridgeableTokens,
  BridgeType,
} from "../types"
import { bigIntToFloat } from "../utils"

export const useSegmentPageView = () => {
  useEffect(() => {
    const segment = AnalyticsBrowser.load({ writeKey: SEGMENT_KEY! })
    segment.page()
  }, [])
}

export const useSegmentButtonClickedEvent = () => {
  const segment = AnalyticsBrowser.load({ writeKey: SEGMENT_KEY! })

  const username = useDappSelector(selectUsername)
  const walletAddress = useDappSelector(selectWalletAddress)

  const emitButtonClickedSegmentEvent = useCallback(
    (analyticsId: string) => {
      if (!username || !walletAddress) return

      segment.track("Button Clicked", {
        analytics_id: analyticsId,
        username,
        wallet_address: walletAddress,
      })
    },
    [segment, username, walletAddress],
  )

  return { emitButtonClickedSegmentEvent }
}

export const useSegmentWalletConnectedEvent = () => {
  const segment = AnalyticsBrowser.load({ writeKey: SEGMENT_KEY! })
  const loadTimeQS = useRef("")

  useLayoutEffect(() => {
    loadTimeQS.current = window.location.search
  }, [])

  const emitWalletConnectedSegmentEvent = useCallback(
    (authId: string, walletAddress: string, username: string) => {
      if (authId) {
        segment.identify(authId, {
          wallet_address: walletAddress,
          wallet_type: null,
          discord_name: null,
          email: null,
          username,
          userId: authId,
          name: username,
        })
      }

      segment.track("Wallet Connected", {
        wallet_address: walletAddress,
        wallet_type: null,
        username,
        userId: username,
      })
    },
    [segment],
  )

  return {
    emitWalletConnectedSegmentEvent,
  }
}

export const useSegmentSignUpEvent = () => {
  const segment = AnalyticsBrowser.load({ writeKey: SEGMENT_KEY! })

  const emitSignUpCompletedSegmentEvent = useCallback(
    (authId: string, walletAddress: string, username: string) => {
      segment.identify(authId, {
        username,
        wallet_address: walletAddress,
        wallet_type: null,
        discord_name: null,
        email: null,
      })

      segment.track("Signed Up", {
        username,
        primary_wallet_address: walletAddress,
      })
    },
    [segment],
  )

  const emitSignUpDiscordAddedSegmentEvent = useCallback(
    (authId: string, discordName: string) => {
      segment.identify(authId, { discord_name: discordName })
      segment.track("Signed Up - Discord", {
        discord_name: discordName,
      })
    },
    [segment],
  )

  return {
    emitSignUpCompletedSegmentEvent,
    emitSignUpDiscordAddedSegmentEvent,
  }
}

export const useSegmentUsernameSetEvent = () => {
  const segment = AnalyticsBrowser.load({ writeKey: SEGMENT_KEY! })

  const emitUsernameSetSegmentEvent = useCallback(
    (authId: string, username: string) => {
      segment.identify(authId, { username })
      segment.track("Username Set", { username })
    },
    [segment],
  )

  return { emitUsernameSetSegmentEvent }
}

export const useSegmentBorrowEvent = () => {
  const segment = AnalyticsBrowser.load({ writeKey: SEGMENT_KEY! })

  const emitBorrowedSegmentEvent = useCallback(
    (walletAddress: string, borrowAmount: string, collateralAmount: string) => {
      segment.track("MUSD Borrowed", {
        wallet_address: walletAddress,
        wallet_type: null,
        borrow_amount: borrowAmount,
        collateral_amount: collateralAmount,
        is_borrower: true,
      })
    },
    [segment],
  )

  return { emitBorrowedSegmentEvent }
}

export const useSegmentRepayEvent = () => {
  const segment = AnalyticsBrowser.load({ writeKey: SEGMENT_KEY! })

  const emitBorrowRepaySegmentEvent = useCallback(
    (eventProps: BorrowRepayPosthogEventProps) =>
      segment.track("Loan Repaid", eventProps),
    [segment],
  )

  return {
    emitBorrowRepaySegmentEvent,
  }
}

export const useSegmentBorrowMoreEvent = () => {
  const segment = AnalyticsBrowser.load({ writeKey: SEGMENT_KEY! })

  const emitBorrowMoreIncreaseCollSegmentEvent = useCallback(
    (
      walletAddress: string,
      borrowedAmount: string,
      collateralAmount: string,
    ) => {
      segment.track("Loan Increased", {
        wallet_address: walletAddress,
        borrow_amount: borrowedAmount,
        collateral_amount: collateralAmount,
      })
    },
    [segment],
  )

  const emitBorrowMoreReduceRatioSegmentEvent = useCallback(
    (walletAddress: string, borrowedAmount: string) => {
      segment.track("Loan Increased - Reduced ratio", {
        wallet_address: walletAddress,
        borrow_amount: borrowedAmount,
      })
    },
    [segment],
  )

  return {
    emitBorrowMoreIncreaseCollSegmentEvent,
    emitBorrowMoreReduceRatioSegmentEvent,
  }
}

export const useSegmentCollateralManagementEvent = () => {
  const segment = AnalyticsBrowser.load({ writeKey: SEGMENT_KEY! })

  const emitCollateralDepositSegmentEvent = useCallback(
    (eventProps: BorrowCollateralChangePosthogEventProps) => {
      segment.track("Loan Collateral Deposited", eventProps)
    },
    [segment],
  )

  const emitCollateralWithdrawSegmentEvent = useCallback(
    (eventProps: BorrowCollateralChangePosthogEventProps) => {
      segment.track("Loan Collateral Withdrawn", eventProps)
    },
    [segment],
  )

  return {
    emitCollateralDepositSegmentEvent,
    emitCollateralWithdrawSegmentEvent,
  }
}

export const useSegmentMarketTransactionEvent = () => {
  const segment = AnalyticsBrowser.load({ writeKey: SEGMENT_KEY! })
  const account = useAccount()
  const walletAddress = account.address
  const walletProvider = account.connector?.name
  const { marketItemId } = useParams({ strict: false })

  const itemName = useMemo(() => {
    const itemById = MARKET_ITEMS.find(
      (marketItem) => marketItem.id === marketItemId,
    )

    return itemById?.id
  }, [marketItemId])

  /**
   * Emit event when user successfully purchases an item. Function retrieves
   * user data based on connected wallet.
   */
  const emitTransactionSuccessSegmentEvent = useCallback(
    (type: "donation" | "purchase", amount: string | bigint = 0n) => {
      if (!itemName) return

      const eventName = type === "donation" ? "MUSD Donated" : "Item Purchased"

      segment.track(eventName, {
        item_name: itemName,
        amount: bigIntToFloat(BigInt(amount)),
        wallet_address: walletAddress,
        wallet_provider: walletProvider,
      })
    },
    [itemName, segment, walletAddress, walletProvider],
  )

  return {
    emitTransactionSuccessSegmentEvent,
  }
}

export const useSegmentSwitchToTestnetEvent = () => {
  const segment = AnalyticsBrowser.load({ writeKey: SEGMENT_KEY! })

  const emitSwitchToTestnetEvent = useCallback(() => {
    segment.track("Switch to Testnet")
  }, [segment])

  return { emitSwitchToTestnetEvent }
}

export const useSegmentBridgeEvent = () => {
  const segment = AnalyticsBrowser.load({ writeKey: SEGMENT_KEY! })

  const emitAddFundsCompletedSegmentEvent = useCallback(
    (
      assetType: BridgeableTokens,
      assetAmount: string,
      walletAddress: string,
      bridgeUsed: BridgeType,
      networkFees: string,
      serviceFees: string,
    ) => {
      segment.track("Funds Added", {
        asset_type: assetType,
        asset_amount: assetAmount,
        wallet_address: walletAddress,
        wallet_type: null,
        bridge_used: bridgeUsed,
        network_fees: networkFees,
        service_fees: serviceFees,
      })
    },
    [segment],
  )

  return { emitAddFundsCompletedSegmentEvent }
}
